import type React from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { ResponseListCard } from './components/ResponseListCard';
import { useResponseList } from './hooks/useResponseList';

export const ResponseList: React.FC = () => {
	const {
		data: { displayName, responses, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = useResponseList();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate } = useI18n();

	if (!responses.length) return null;

	return (
		<div data-qa="dm-responses-content" ref={containerRef}>
			<Text as="h3" variant="heading" className="mb-6">
				<Translate value="corgi.decision_maker_profile.responses_list.header" replacements={{ displayName }} />
			</Text>

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{responses.map((response, index) =>
					response.petition ? (
						<div key={response.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.decision_maker_profile.responses_list.fetched_more"
										count={responses.length - lastEvent.previousItemsCount}
										replacements={{ count: responses.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<ResponseListCard
								responseId={response.id}
								message={response.message}
								petitionHref={`/p/${encodeURIComponent(response.petition.slug)}`}
								petitionAsk={response.petition.ask}
								petitionId={response.petition.id}
								ref={getListItemRef(index)}
							/>
						</div>
					) : null,
				)}
			</div>

			{nextPageLoading && (
				<div className="mt-16">
					<div className="sr-only" role="alert">
						<Translate value="corgi.decision_maker_profile.responses_list.fetching_more" />
					</div>
					<Spinner className="mx-auto" ref={loadingRef} data-testid="load-more-btn" />
				</div>
			)}

			{hasNextPage && !nextPageLoading && (
				<div className="flex justify-center">
					<Button
						className="mt-16 basis-full md:basis-1/3"
						size="small"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: responses.length });
							loadMore();
						}}
						data-qa="dm-responses-load-more"
						ref={loadMoreRef}
					>
						{translate('fe.components.load_more.load_more')}
					</Button>
				</div>
			)}
		</div>
	);
};
